import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Stack,
  Button,
  Paper,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store/store';
import { setSelectedYear } from '../../store/appSlice';
import { setHasInitializedYear } from '../../store/dataExplorerSlice';
import { useAccountContext } from '../../hooks/useAccountContext';
import { endpoints } from '../../services/apiClient';
import SummaryCard from '../common/SummaryCard';
import NEEChart from '../charts/NEEChart';
import YearPicker from '../common/YearPicker';
import DownloadDataModal from '../common/DownloadDataModal';
import { ChartType, NEEDisplayMode } from '../../types/enums';
import { AnnualNEEData, MonthlyNEEData } from '../../types/schema';
import { formatPolygonCount } from '../../utils/formatters';
import VerticalAlignBottomOutlinedIcon from '@mui/icons-material/VerticalAlignBottomOutlined';
import { SummarySection } from '../common/SummarySection';

const DashboardContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  height: '100%',
  overflow: 'auto',
}));

const HeaderSection = styled(Stack)(({ theme }) => ({
  marginBottom: theme.spacing(3),
}));

const ChartSection = styled(Stack)(({ theme }) => ({
  gap: theme.spacing(3),
}));

const YearFilterSection = styled(Stack)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

const DataSourcesFooter = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginTop: theme.spacing(3),
  backgroundColor: theme.palette.grey[50],
  borderRadius: theme.shape.borderRadius,
}));

interface KPIData {
  polygonCount: number;
  totalArea: number;
  neeTotal: number;
  neeIntensity: number;
}


const DashboardView: React.FC = () => {
  const dispatch = useDispatch();
  const { selectedYear } = useSelector((state: RootState) => state.app);
  const { hasInitializedYear } = useSelector((state: RootState) => state.dataExplorer);

  const { accountId } = useAccountContext();

  const [kpiData, setKpiData] = useState<KPIData | null>(null);
  const [kpiError, setKpiError] = useState<string | null>(null);
  const [annualNeeData, setAnnualNeeData] = useState<AnnualNEEData[]>([]);
  const [monthlyNeeData, setMonthlyNeeData] = useState<MonthlyNEEData[]>([]);
  const [annualDisplayMode, setAnnualDisplayMode] = useState<NEEDisplayMode>(NEEDisplayMode.TOTAL);
  const [monthlyDisplayMode, setMonthlyDisplayMode] = useState<NEEDisplayMode>(NEEDisplayMode.TOTAL);
  const [loading, setLoading] = useState(false);
  const [availableYears, setAvailableYears] = useState<number[]>([]);
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);

  // Fetch available years on mount and when account changes
  useEffect(() => {
    const fetchAvailableYears = async () => {
      try {
        const response = await endpoints.dashboardAvailableYears(accountId);
        if (response.years && response.years.length > 0) {
          setAvailableYears(response.years);

          // Set selectedYear to latest available year on initial load or account change
          if (!hasInitializedYear && response.max_year ) {
            dispatch(setSelectedYear(response.max_year));
            dispatch(setHasInitializedYear(true));
          }
        }
      } catch (error) {
        console.error('Failed to fetch available years:', error);
      }
    };

    fetchAvailableYears();
  }, [accountId,hasInitializedYear, dispatch ]);

  // Fetch KPI and chart data when selectedYear changes
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setKpiError(null);
      try {
        // Fetch all data in parallel
        const [kpiResponse, annualResponse, monthlyResponse] = await Promise.all([
          endpoints.dashboardKpis(selectedYear, selectedYear, accountId),
          endpoints.analyticsAnnualNee(selectedYear, selectedYear, undefined, accountId),
          endpoints.analyticsMonthlyNee(selectedYear, selectedYear, undefined, accountId),
        ]);

        // Set KPI data
        setKpiData(kpiResponse);

        // Set annual and monthly data directly
        setAnnualNeeData(annualResponse.data);
        setMonthlyNeeData(monthlyResponse.data);

      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to fetch dashboard data';
        setKpiError(errorMessage);
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedYear, accountId]);

  const handleYearChange = (year: number) => {
    dispatch(setSelectedYear(year));
  };

  const handleAnnualDisplayModeChange = (mode: NEEDisplayMode) => {
    setAnnualDisplayMode(mode);
  };

  const handleMonthlyDisplayModeChange = (mode: NEEDisplayMode) => {
    setMonthlyDisplayMode(mode);
  };

  const handleDownloadClick = () => {
    setDownloadModalOpen(true);
  };

  const handleChartDownload = (chartType: string) => {
    console.log(`Downloading ${chartType} chart data...`);
  };

  return (
    <DashboardContainer>
      <HeaderSection>
        <Stack direction="row" justifyContent="space-between" alignItems=" center">
          <Stack direction="row" alignItems="center" spacing={2}>
            <Typography variant="h4" sx={{ fontWeight: 600 }}>
              Dashboard
            </Typography>
          </Stack>
          <Button
            variant="contained"
            startIcon={<VerticalAlignBottomOutlinedIcon />}
            onClick={handleDownloadClick}
          >
            Download Data
          </Button>
        </Stack>
      </HeaderSection>

      <YearFilterSection direction="row" alignItems="center" spacing={2}>
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
          Showing Data For:
        </Typography>
        <YearPicker
          label="Year"
          value={new Date(selectedYear, 0, 1)}
          onChange={(date) => date && handleYearChange(date.getFullYear())}
          minDate={new Date(2000, 0, 1)}
          maxDate={new Date()}
          availableYears={availableYears.length > 0 ? availableYears : undefined}
        />
      </YearFilterSection>

      <SummarySection>
        <SummaryCard
          title="Total Number of Polygons"
          value={kpiData ? formatPolygonCount(kpiData.polygonCount) : '--'}
          loading={loading}
          tooltip="A polygon is a single farm, field, or forest"
        />
        <SummaryCard
          title="Total Area"
          value={kpiData?.totalArea ?? '--'}
          unit="ha"
          loading={loading}
        />
        <SummaryCard
          title="NEE Total"
          value={kpiData?.neeTotal ?? '--'}
          unit="tCO2"
          isNegative={true}
          loading={loading}
          tooltip="Sum of NEE values for all polygons in the selected year. A negative value means net CO2 sequestration, a positive value net CO2 emission."
        />
        <SummaryCard
          title="NEE Intensity"
          value={kpiData?.neeIntensity ?? '--'}
          unit="tCO2/ha/yr"
          isNegative={true}
          loading={loading}
          tooltip="Average NEE performance per hectare for all polygons in the selected year. A negative value means net CO2 sequestration, a positive value net CO2 emission."
        />
      </SummarySection>

      {kpiError && (
        <Typography color="error" sx={{ mb: 2 }}>
          Error loading KPI data: {kpiError}
        </Typography>
      )}

      <ChartSection>
        <NEEChart
          title="Monthly NEE"
          chartType={ChartType.MONTHLY}
          data={monthlyNeeData}
          displayMode={monthlyDisplayMode}
          onDisplayModeChange={handleMonthlyDisplayModeChange}
          onDownload={() => handleChartDownload('monthly')}
          height={400}
          loading={loading}
        />
      </ChartSection>

      <DataSourcesFooter>
        <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
          Data sources: FLUXNET2015, AmeriFlux, AgERA5, Landsat
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Licenses: CC BY 4.0 (except Landsat, Public Domain)
        </Typography>
      </DataSourcesFooter>

      <DownloadDataModal
        open={downloadModalOpen}
        onClose={() => setDownloadModalOpen(false)}
        availableYears={availableYears}
        is_geometry={false}
      />
    </DashboardContainer>
  );
};

export default DashboardView;
